import { useMutation, useQuery } from "@apollo/client/react"; import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import CardBox from "../../CardBox/CardBox";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import SAVE_VISITA_TELE_ENFERMERIA_AE, { adaptAddVisitaTeleEnfermeriaAEInput } from "../../../../../../data/graphQL/mutations/Visitas/saveAcceso";
import { useVisita } from "../../../../../../context/VisitaContext";

import { useStepSave } from "../../../../hooks/useStepSave";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { GET_COMBO_VISITA_VALOR, adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";

const ID_COMBO_METODO_ADMINISTRACION = 4;

export default function Acceso({ esUrgencia, readOnly, onSaveRef, onExposeFormatted }) {
  const [formData, setFormData] = useState({});
  const [metodoAdministracion, setMetodoAdministracion] = useState([]);

  const { visita, setVisita } = useVisita();

  const { data: dataComboVisitaValor } = useQuery(GET_COMBO_VISITA_VALOR, {
    variables: { idComboVisita: ID_COMBO_METODO_ADMINISTRACION },
  });

  useEffect(() => {
    if (!dataComboVisitaValor) return;
    setMetodoAdministracion(adaptCombosVisitas(dataComboVisitaValor));
  }, [dataComboVisitaValor]);

  //QUERY y MUTATION
  const [save] = useMutation(SAVE_VISITA_TELE_ENFERMERIA_AE);

  const [bombaId, gravedadId] = useMemo(() => {
    const bomba = metodoAdministracion.find((metodo) => metodo.displayName?.toLowerCase() === "bomba");
    const gravedad = metodoAdministracion.find((metodo) => metodo.displayName?.toLowerCase() === "gravedad");
    return [bomba?.id, gravedad?.id];
  }, [metodoAdministracion]);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };


        switch (input.name) {
          case "usaAlimentoPor":
            newInput.options = metodoAdministracion;
            break;
          case "estaEnchufadaBomba":
          case "funcionamientoAdecuado":
          case "mantieneCondicionesBomba":
          case "suenanAlarmas":
          case "bombaTiene":
          case "reemplazoBomba":
          case "velocidadFormula":
          case "mantenimientoBomba":
          case "alarmasSuenan":
            newInput.hidden = !formData.usaAlimentoPor || formData.usaAlimentoPor !== bombaId;
            break;

          case "observacionGravedad":
            newInput.hidden = !formData.usaAlimentoPor || formData.usaAlimentoPor !== gravedadId;
            break;

          default:
            break;
        }

        if (esUrgencia) newInput.required = false;

        if (readOnly) newInput.disabled = true;

        return newInput;
      });

      group.hidden = !((!group.tiposAcceso || group.tiposAcceso.includes(formData.tipoAcceso)) && group.inputs.some((input) => !input.hidden));

      return {
        ...group,
        inputs: formattedInputs
      };
    });
  }, [
    readOnly,
    esUrgencia,
    formData.usaAlimentoPor,
    formData.tipoAcceso,
    metodoAdministracion,
    bombaId,
    gravedadId
  ]);

  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });
    const { data } = await save({ variables: { input: adaptAddVisitaTeleEnfermeriaAEInput(formData) } });

    const idTeleEnfermeriaAENueva = data?.saveVisitaTeleEnfermeriaAE?.idTeleEnfermeriaAE;
    if (!idTeleEnfermeriaAENueva) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData,
      idTeleEnfermeriaAE: idTeleEnfermeriaAENueva
    }));

  });

  useEffect(() => {
    setFormData(visita);
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: 'acceso',
      stepName: 'Acceso',
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input) =>
                  <Grid size={{ xs: 12 }} key={input.name}>
                    <RenderInputByTypeMobile
                      input={input}
                      value={formData[input.name]}
                      handleChange={handleChange} />

                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "Acceso Gastro / Yeyuno",
    tiposAcceso: ["Gastrostomía", "Yeyunostomía"],
    inputs: [
      {
        name: "accesoDescripcion",
        displayName: "¿Cómo se encuentra el acceso?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "notaAlteracion",
        displayName: "¿Nota alguna alteración que le llame la atención?",
        longLabel: true,
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "observaTitleAcceso",
        displayName: "Observa",
        type: "text",
        sxText: { fontWeight: "bold", fontSize: "15px" }
      },
      {
        name: "supuracion",
        displayName: "Supuración",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "eritemas",
        displayName: "Eritemas",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "granulomasFuga",
        displayName: "Granulomas o fuga de alimento",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "resistenciaPermeabilidad",
        displayName: "¿Tiene resistencia a la permeabilidad?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "formaLavadoAcceso",
        displayName: "¿Cómo realiza el lavado del acceso?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "frecLavadoAcceso",
        displayName: "Mencione cómo cura el acceso y con qué frecuencia",
        type: "long_string",
        maxLength: 250,
        required: true
      }]

  },

  {
    title: "Acceso SNG / SNY",
    tiposAcceso: ["SNG", "SNY"],
    inputs: [
      {
        name: "permeableSonda",
        displayName: "¿Está permeable?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "fijacionSonda",
        displayName: "¿Cómo se encuentra la fijación?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "marcaReferencia",
        displayName:
          "¿La marca de referencia para detectar desplazamiento está?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "desplazoSonda",
        displayName: "¿En algún momento se desplazó?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "accionDesplazo",
        displayName: "¿Qué hizo si esto pasó?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "frecuenciaLavadoSonda",
        displayName:
          "¿Con qué frecuencia realiza el lavado del acceso?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "formaLavadoSonda",
        displayName: "¿De qué forma?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "cambioSonda",
        displayName:
          "¿Cuándo se cambió la sonda por última vez?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "quienCambioSonda",
        displayName: "¿Quién lo hizo?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "usaAlimentoPor",
        displayName: "¿Usa el alimento por gravedad o por bomba?",
        type: "select",
        required: true
      },
      {
        name: "estaEnchufadaBomba",
        displayName: "¿Está enchufada la bomba?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "funcionamientoAdecuado",
        displayName: "¿Su funcionamiento es adecuado?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "mantenimientoBomba",
        displayName: "¿Cómo mantiene en condiciones su bomba?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "alarmasSuenan",
        displayName: "¿Suenan mucho las alarmas?",
        type: "boolean",
        lightTitle: true,
        required: true
      },
      {
        name: "bombaTiene",
        displayName: "¿Qué bomba tiene?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "reemplazoBomba",
        displayName:
          "¿Tuvo que reemplazar su bomba por mal funcionamiento en este tiempo?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "velocidadFormula",
        displayName: "¿A qué velocidad administra la fórmula?",
        type: "long_string",
        maxLength: 250,
        required: true
      },
      {
        name: "observacionGravedad",
        displayName: "Observación",
        type: "long_string",
        maxLength: 250,
        required: true
      }
    ]
  }];